import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { GitBranch, Database, ArrowRight, LayoutDashboard, Bell, CalendarClock, Loader2, AlertTriangle } from "lucide-react";

export function LineageDialog({ open, onOpenChange, dataset }) {
  const [data, setData] = useState(null);
  const [field, setField] = useState("");
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  useEffect(() => {
    if (open && dataset?.id) {
      setData(null); setImpact(null); setField("");
      api.get(`/datasets/${dataset.id}/lineage`).then(({ data: d }) => setData(d)).catch(() => {});
    }
  }, [open, dataset?.id]);

  useEffect(() => {
    if (!field || !dataset?.id) { setImpact(null); return; }
    setImpactLoading(true);
    api.get(`/datasets/${dataset.id}/lineage/impact`, { params: { field } })
      .then(({ data: d }) => setImpact(d)).catch(() => setImpact(null))
      .finally(() => setImpactLoading(false));
  }, [field, dataset?.id]);

  const t = data?.transformations;
  const down = data?.downstream;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="lineage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><GitBranch className="w-5 h-5 text-[color:var(--brand-ocean)]" />Lineage — {dataset?.name}</DialogTitle>
          <DialogDescription>Where this data comes from, how it is transformed, and everything that depends on it.</DialogDescription>
        </DialogHeader>
        {!data && <div className="flex items-center gap-2 text-sm text-[color:var(--ink-3)] py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Tracing lineage…</div>}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-start">
            <div className="glass-card p-3" data-testid="lineage-upstream">
              <div className="text-[11px] font-semibold text-[color:var(--ink-3)] uppercase mb-2">Upstream</div>
              <div className="text-sm text-[color:var(--ink)] flex items-center gap-1.5"><Database className="w-4 h-4 text-[color:var(--brand-mint)]" />{data.upstream.mode === "live" ? "Live connection" : "File snapshot"}</div>
              {data.upstream.connection && (
                <div className="text-xs text-[color:var(--ink-2)] mt-1">{data.upstream.connection.name} <Badge variant="outline" className="text-[9px] ml-1">{data.upstream.connection.db_type}</Badge>
                  {data.upstream.object && <div className="text-[color:var(--ink-3)] mt-0.5">→ {data.upstream.object}</div>}
                </div>
              )}
              {data.upstream.mode !== "live" && <div className="text-xs text-[color:var(--ink-3)] mt-1">{data.dataset.row_count?.toLocaleString()} rows · v{data.dataset.version}</div>}
            </div>
            <ArrowRight className="w-4 h-4 text-[color:var(--ink-3)] mt-8 hidden md:block" />
            <div className="glass-card p-3" data-testid="lineage-transforms">
              <div className="text-[11px] font-semibold text-[color:var(--ink-3)] uppercase mb-2">Transformations</div>
              <div className="space-y-1 text-xs text-[color:var(--ink-2)]">
                <div>Prep steps: <b>{t.prep_steps.length}</b>{t.prep_steps.length > 0 && <span className="text-[color:var(--ink-3)]"> ({t.prep_steps.map((s) => s.type).join(", ")})</span>}</div>
                <div>Calculated fields: <b>{t.calc_fields.length}</b>{t.calc_fields.length > 0 && <span className="text-[color:var(--ink-3)]"> ({t.calc_fields.join(", ")})</span>}</div>
                {t.model && (
                  <div className="pt-1 border-t border-[color:var(--glass-border)] mt-1">
                    Semantic model: <b>{t.model.measures}</b> measures ({t.model.calculated_measures} calc), <b>{t.model.dimensions}</b> dims
                    {(t.model.rls_rules > 0 || t.model.cls_rules > 0) && <div className="text-[color:var(--danger)]">Security: {t.model.rls_rules} RLS · {t.model.cls_rules} CLS rules</div>}
                  </div>
                )}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-[color:var(--ink-3)] mt-8 hidden md:block" />
            <div className="glass-card p-3" data-testid="lineage-downstream">
              <div className="text-[11px] font-semibold text-[color:var(--ink-3)] uppercase mb-2">Downstream</div>
              <div className="space-y-1.5 text-xs">
                {down.dashboards.map((d) => (
                  <div key={d.id} className="text-[color:var(--ink-2)]"><LayoutDashboard className="w-3.5 h-3.5 inline mr-1 text-[color:var(--brand-ocean)]" />{d.name} <span className="text-[color:var(--ink-3)]">({d.widgets.length} widgets)</span></div>
                ))}
                {down.alerts.map((a) => (
                  <div key={a.id} className="text-[color:var(--ink-2)]"><Bell className="w-3.5 h-3.5 inline mr-1 text-[color:var(--brand-mint)]" />{a.name}</div>
                ))}
                {down.schedules.map((s) => (
                  <div key={s.id} className="text-[color:var(--ink-2)]"><CalendarClock className="w-3.5 h-3.5 inline mr-1 text-[color:var(--ink-3)]" />{s.dashboard} · {s.frequency} {s.format}</div>
                ))}
                {!down.dashboards.length && !down.alerts.length && !down.schedules.length && <div className="text-[color:var(--ink-3)]">Nothing depends on this dataset yet.</div>}
              </div>
            </div>
          </div>
        )}
        {data && (
          <div className="glass-card p-3 mt-1" data-testid="impact-section">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs font-semibold text-[color:var(--ink-2)] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />Impact analysis — what breaks if I change:</Label>
              <Select value={field} onValueChange={setField}>
                <SelectTrigger data-testid="impact-field-select" className="w-52 h-8 text-xs bg-[color:var(--surface)]"><SelectValue placeholder="pick a column" /></SelectTrigger>
                <SelectContent className="max-h-64">{(dataset?.schema || []).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {impactLoading && <Loader2 className="w-4 h-4 animate-spin text-[color:var(--ink-3)]" />}
              {impact && <Badge data-testid="impact-count" className={impact.impact_count > 0 ? "bg-[color:rgba(255,171,0,0.16)] text-[#b7791f] border-0" : "bg-[color:rgba(0,194,168,0.14)] text-[color:var(--success)] border-0"}>{impact.impact_count} dependencies</Badge>}
            </div>
            {impact && impact.impact_count > 0 && (
              <div className="mt-2 text-xs text-[color:var(--ink-2)] space-y-1">
                {impact.model_measures.length > 0 && <div>Model measures: <b>{impact.model_measures.join(", ")}</b>{impact.calculated_measure_dependents.length > 0 && <span> → feeds calc measures <b>{impact.calculated_measure_dependents.join(", ")}</b></span>}</div>}
                {impact.model_dimensions.length > 0 && <div>Model dimensions: <b>{impact.model_dimensions.join(", ")}</b></div>}
                {impact.calc_fields.length > 0 && <div>Calculated fields: <b>{impact.calc_fields.join(", ")}</b></div>}
                {impact.prep_step_indices.length > 0 && <div>Prep steps: #{impact.prep_step_indices.map((i) => i + 1).join(", #")}</div>}
                {(impact.rls_rules.length > 0 || impact.cls_rules.length > 0) && <div className="text-[color:var(--danger)]">Security rules: {impact.rls_rules.length} RLS, {impact.cls_rules.length} CLS</div>}
                {impact.widgets.length > 0 && (
                  <div>Widgets ({impact.widgets.length}): {impact.widgets.slice(0, 8).map((w, i) => <span key={i} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-[color:var(--surface-2)]">{w.dashboard} › {w.widget} <span className="text-[color:var(--ink-3)]">({w.uses.join("/")})</span></span>)}{impact.widgets.length > 8 && "…"}</div>
                )}
                {impact.alerts.length > 0 && <div>Alerts: {impact.alerts.map((a) => a.name).join(", ")}</div>}
              </div>
            )}
            {impact && impact.impact_count === 0 && <p className="text-xs text-[color:var(--ink-3)] mt-2">Nothing references this column — safe to rename or remove.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
